import { useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import CreatableSelect from '@/components/CreatableSelect';
import { useOffice } from '@/hooks/useOffice';
import { logActivity } from '@/lib/activityLog';

interface Props {
  onClose: () => void;
  onDone: () => void;
  allMediaSets?: string[];
  allBackupSets?: string[];
  allTapeTypes?: string[];
  allLocations?: string[];
  allBoxNumbers?: string[];
}

const MAX_BATCH = 1000;

// Parse "HC010L6" style labels into a prefix + zero-padded number + suffix
// and generate the full range between `first` and `last`.
function generateLabels(first: string, last: string): string[] {
  const a = first.trim();
  const b = last.trim();
  if (!a || !b) return [];
  if (a === b) return [a];

  let p = 0;
  while (p < a.length && p < b.length && a[p] === b[p]) p += 1;
  let s = 0;
  while (s < a.length - p && s < b.length - p && a[a.length - 1 - s] === b[b.length - 1 - s]) s += 1;

  const prefix = a.slice(0, p);
  const suffix = a.slice(a.length - s);
  const midA = a.slice(p, a.length - s);
  const midB = b.slice(p, b.length - s);

  if (!midA || !midB || !/^\d+$/.test(midA) || !/^\d+$/.test(midB)) return [];

  const start = parseInt(midA, 10);
  const end = parseInt(midB, 10);
  if (end < start) return [];
  if (end - start + 1 > MAX_BATCH) return [];

  const width = Math.max(midA.length, midB.length);
  const labels: string[] = [];
  for (let n = start; n <= end; n += 1) {
    labels.push(prefix + String(n).padStart(width, '0') + suffix);
  }
  return labels;
}

export default function BatchCreateModal({ onClose, onDone, allMediaSets = [], allBackupSets = [], allTapeTypes = [], allLocations = [], allBoxNumbers = [] }: Props) {
  const { currentOfficeId, currentOffice } = useOffice();
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [form, setForm] = useState<{
    media_set: string; backup_set: string; tape_type: string; location: string; box_number: string; modified_date: string; notes: string; needs_replacement: boolean; damaged: boolean;
  }>({
    media_set: '', backup_set: '', tape_type: '', location: currentOffice?.name || '', box_number: '', modified_date: '', notes: '', needs_replacement: false, damaged: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = <K extends keyof typeof form>(k: K, v: any) => setForm((s) => ({ ...s, [k]: v }));

  const locationDefaultOptions = currentOffice?.name
    ? [currentOffice.name, 'Backup Tape Library']
    : ['Backup Tape Library'];

  const labels = useMemo(() => generateLabels(first, last), [first, last]);
  const parseOk = first.trim() && last.trim() && labels.length > 0;

  const preview = useMemo(() => {
    if (labels.length === 0) return '';
    if (labels.length <= 6) return labels.join(', ');
    return `${labels.slice(0, 4).join(', ')}, … , ${labels[labels.length - 1]}`;
  }, [labels]);

  const create = async () => {
    if (!parseOk) { setErr('Please enter a valid first and last label.'); return; }
    if (!currentOfficeId) { setErr('No office selected.'); return; }
    setSaving(true); setErr('');

    const base = {
      media_set: form.media_set || null,
      backup_set: form.backup_set || null,
      modified_date: form.modified_date || null,
      tape_type: form.tape_type || null,
      location: form.location || null,
      box_number: form.box_number || null,
      notes: form.notes || null,
      needs_replacement: !!form.needs_replacement,
      damaged: !!form.damaged,
      updated_at: new Date().toISOString(),
    };

    const rows = labels.map((label) => ({
      ...base,
      media_label: label,
      office_id: currentOfficeId,
      is_new: true,
    }));

    const { error } = await supabase.from('tapes').insert(rows);
    if (error) { setSaving(false); setErr(error.message); return; }

    // Best-effort: ensure the assigned box exists in the boxes table once.
    if (base.box_number) {
      try {
        const { data: existingBox } = await supabase
          .from('boxes')
          .select('id')
          .eq('box_number', base.box_number)
          .eq('office_id', currentOfficeId)
          .maybeSingle();
        if (!existingBox) {
          await supabase.from('boxes').insert({
            box_number: base.box_number,
            location: base.location,
            office_id: currentOfficeId,
          });
        }
      } catch (e) {
        // Box sync is best-effort.
      }
    }

    setSaving(false);
    await logActivity('create_tapes_batch', `Created ${rows.length} tape(s): ${labels[0]} → ${labels[labels.length - 1]}`);
    onDone();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-sm">Batch Create Tapes</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 cursor-pointer">
            <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
          </button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">First Media Label *</label>
              <input
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                placeholder="e.g. HC010L6"
                className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 font-mono"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Last Media Label *</label>
              <input
                value={last}
                onChange={(e) => setLast(e.target.value)}
                placeholder="e.g. HC018L6"
                className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 font-mono"
              />
            </div>
          </div>

          {first.trim() && last.trim() && !parseOk && (
            <div className="text-xs text-amber-600">Could not derive a range — check that only the number part changes between the two labels.</div>
          )}
          {labels.length > 0 && (
            <div className="text-xs text-slate-600 bg-slate-50 rounded-md px-3 py-2 border border-slate-100">
              <span className="font-medium text-slate-800">{labels.length}</span> tape(s) will be created:{' '}
              <span className="font-mono text-slate-700">{preview}</span>
            </div>
          )}

          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-400 mb-2">The following settings apply to the whole batch.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Media Set</label>
                <CreatableSelect
                  value={form.media_set}
                  onChange={(v) => set('media_set', v)}
                  options={allMediaSets}
                  defaultOptions={['Monthly Full Backup', 'Weekly Full Backup', 'Daily Backup', 'Yearly Backup', 'CTU Project Backup', 'SHA Project Backup']}
                  placeholder="Select or add Media Set"
                  clearable
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Backup Set</label>
                <CreatableSelect
                  value={form.backup_set}
                  onChange={(v) => set('backup_set', v)}
                  options={allBackupSets}
                  defaultOptions={[]}
                  placeholder="Select or add Backup Set"
                  clearable
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Tape Type</label>
                <CreatableSelect
                  value={form.tape_type}
                  onChange={(v) => set('tape_type', v)}
                  options={allTapeTypes}
                  defaultOptions={['Daily', 'Weekly', 'Monthly', 'Yearly']}
                  placeholder="Select or add Tape Type"
                  clearable
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Location</label>
                <CreatableSelect
                  value={form.location}
                  onChange={(v) => set('location', v)}
                  options={allLocations}
                  defaultOptions={locationDefaultOptions}
                  placeholder="Select or add Location"
                  clearable
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Modified Date</label>
                <input
                  type="date"
                  value={form.modified_date}
                  onChange={(e) => set('modified_date', e.target.value)}
                  className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Box Number</label>
                <CreatableSelect
                  value={form.box_number}
                  onChange={(v) => set('box_number', v)}
                  options={allBoxNumbers}
                  defaultOptions={['Backup Tape Library']}
                  placeholder="Select or add Box Number"
                  clearable
                  emptyLabel="(None)"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Notes</label>
                <textarea
                  value={form.notes} onChange={(e) => set('notes', e.target.value)}
                  rows={2}
                  className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 resize-none"
                />
              </div>
              <label className="flex items-center gap-2.5 py-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.needs_replacement}
                  onChange={(e) => set('needs_replacement', e.target.checked)}
                  className="w-4 h-4 cursor-pointer accent-rose-600"
                />
                <span className="text-sm text-slate-700">Needs replacement</span>
              </label>
              <label className="flex items-center gap-2.5 py-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.damaged}
                  onChange={(e) => set('damaged', e.target.checked)}
                  className="w-4 h-4 cursor-pointer accent-red-600"
                />
                <span className="text-sm text-slate-700">Damaged</span>
              </label>
            </div>
          </div>

          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-200 cursor-pointer whitespace-nowrap">Cancel</button>
          <button
            onClick={create}
            disabled={saving || !parseOk}
            className="px-4 py-2 text-sm rounded-md bg-slate-900 text-white cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {saving ? 'Creating...' : `Create ${labels.length || ''} Tapes`}
          </button>
        </div>
      </div>
    </div>
  );
}